import React, { useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Card, Input, Pill, Select } from "../../components/common/Primitives";
import SandboxKycPanel from "../../components/kyc/SandboxKycPanel";
import { ShieldCheck, Upload, Trash2, FileText, Download } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useFocusHighlight from "../../lib/useFocusHighlight";

const KYC_CATEGORIES = [
  "PAN Card", "Aadhaar / Passport", "Entity Incorporation", "SEBI / AIF Registration",
  "NBFC License", "Bank RBI License", "Authorised Signatory Declaration",
  "Fund Deck / Brochure", "Source of Funds Declaration", "Compliance Declaration",
];

const toneFor = (s) => {
  if (s === "Accepted") return "success";
  if (s === "Rejected") return "danger";
  if (s === "On Hold") return "warn";
  if (s === "Approved") return "success";
  if (s === "Under Review") return "warn";
  return "muted";
};

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function InvestorKYC() {
  useFocusHighlight();
  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState(KYC_CATEGORIES[0]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setMe((await api.get("/investor/me")).data);
    setDocs((await api.get("/investor/kyc/documents")).data || []);
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("note", note || "");
      fd.append("file", file);
      await api.post("/investor/kyc/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded.`);
      setFile(null); setNote("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/investor/kyc/documents/${id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitKyc = async () => {
    try {
      await api.post("/investor/kyc/submit");
      toast.success("KYC submitted. Under review.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const status = me?.investor?.kyc_status || "Not Submitted";

  return (
    <DashboardShell title="Investor KYC" subtitle="INVESTOR · COMPLIANCE">
      <Toaster richColors position="top-right" />

      {/* Instant identity via Sandbox — DigiLocker + PAN */}
      <div className="mb-6" data-testid="investor-sandbox-kyc">
        <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mb-2">
          Instant verification · Powered by DigiLocker & Income Tax
        </div>
        <SandboxKycPanel />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card testId="investor-kyc-status">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="text-gold" size={20} />
              <div>
                <div className="overline">Current Status</div>
                <div className="mt-1"><Pill tone={toneFor(status)}>{status}</Pill></div>
              </div>
            </div>
            <p className="text-ink-soft text-sm leading-relaxed">
              Complete investor declaration and upload KYC documents. VenturaZ verifies before granting institutional-tier access to sensitive startup data.
            </p>
            <ul className="mt-4 space-y-1.5 text-xs text-ink-muted">
              <li>• Authorised signatory PAN + Aadhaar/Passport</li>
              <li>• Entity incorporation (LLP/Pvt Ltd/Trust)</li>
              <li>• Regulatory license (SEBI / RBI / AIF as applicable)</li>
              <li>• Source-of-funds declaration</li>
            </ul>
            <Button onClick={submitKyc} className="mt-5 w-full" data-testid="investor-kyc-submit">
              Submit for KYC Review
            </Button>
          </Card>

          <form onSubmit={upload} className="mt-4 border border-white/10 bg-white/70 backdrop-blur p-6 space-y-4" data-testid="investor-kyc-upload-form">
            <div className="overline">Upload Document</div>
            <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)} testId="ikyc-cat">
              {KYC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
            <div>
              <div className="overline mb-1.5">File</div>
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-ink-soft file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-gold file:text-ink-bg file:font-heading file:font-semibold file:cursor-pointer file:hover:bg-gold-hover"
                data-testid="ikyc-file"
              />
              {file && <div className="text-[11px] font-mono text-ink-muted mt-2">{file.name} · {formatBytes(file.size)}</div>}
            </div>
            <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} testId="ikyc-note" />
            <Button type="submit" disabled={busy || !file} className="w-full flex items-center justify-center gap-2" data-testid="ikyc-upload-btn">
              <Upload size={14} /> {busy ? "Uploading…" : "Upload"}
            </Button>
          </form>
        </div>

        <div className="lg:col-span-2 border border-white/10 bg-white/70 backdrop-blur" data-testid="ikyc-docs-list">
          <div className="p-4 border-b border-white/10 overline">KYC Documents · {docs.length}</div>
          {docs.length === 0 ? (
            <div className="p-10 text-center text-ink-muted text-sm">No KYC documents uploaded yet.</div>
          ) : (
            <div className="divide-y divide-white/10">
              {docs.map((d) => (
                <div key={d.doc_id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={16} className="text-gold shrink-0" />
                    <div className="min-w-0">
                      <div className="text-ink text-sm font-heading truncate">{d.filename}</div>
                      <div className="text-[11px] font-mono text-ink-muted uppercase tracking-wider">{d.category}{d.size ? ` · ${formatBytes(d.size)}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Pill tone={toneFor(d.status)}>{d.status}</Pill>
                    <button onClick={() => download(d)} className="text-ink-muted hover:text-gold transition-colors" title="Download" data-testid={`ikyc-download-${d.doc_id}`}><Download size={14} /></button>
                    <button onClick={() => remove(d.doc_id)} className="text-ink-muted hover:text-rose-400 transition-colors" title="Delete" data-testid={`ikyc-delete-${d.doc_id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
